import React from 'react';
import { ChoiceChipProps } from '../../../types/DesignSystem/ChoiceChip';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';

/**
 * ChoiceChip (§ 67) — see the type file for the chip-grammar split.
 *
 * Unselected is the § 66 quiet pair (`ink-secondary` on `surface-sunken`,
 * hover deepens to `tone-neutral-tint` — measured 5.18:1 light / 5.59:1 dark,
 * AA holds through the hover). Selected is the ONE selection grammar (§ 51):
 * accent on its tint, the same statement as a checked menu radio or a chosen
 * Dropdown option. No border in either state — selection changes colour only,
 * so toggling never moves a neighbour.
 */
const ChoiceChip = React.forwardRef<HTMLButtonElement, ChoiceChipProps>(function ChoiceChip({
  label,
  selected,
  tone = 'neutral',
  disabled = false,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* Precedence mirrors `Tag`/`Status`: disabled outranks everything, then the
     selection grammar, then tone. A danger tone only paints the RESTING chip —
     a selected or disabled chip keeps the one grammar those states own (§ 193). */
  /* § 378 — selected is not a colour alone. The tint sits 1.28:1 from the resting ground, and
     both are fills, which forced colors replaces; the inner outline is a shape a reader sees
     without the hue (an OUTLINE, so the chip never grows and no neighbour moves), and in
     forced colors it takes the system Highlight. */
  const colourPair = disabled
    ? 'tw-bg-surface-sunken tw-text-ink-disabled tw-cursor-not-allowed'
    : selected
      ? 'tw-bg-accent-tint tw-text-accent tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-accent forced-colors:tw-outline-[color:Highlight]'
      : tone === 'danger'
        ? 'tw-bg-tone-danger-tint tw-text-tone-danger-ink hover:tw-text-ink'
        : 'tw-bg-surface-sunken tw-text-ink-secondary hover:tw-bg-tone-neutral-tint hover:tw-text-ink';
  return (
    <button
      /* rest first — see the rule in Button.tsx. */
      {...rest}
      ref={ref}
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      data-tone={tone === 'danger' ? 'danger' : undefined}
      data-testid={dataTestId}
      /* `select-none` (§ 235): a toggled chip is re-clicked constantly. */
      className={withClassName(`tw-inline-flex tw-items-center tw-justify-center tw-min-h-7 tw-px-2 tw-rounded-ctl tw-text-caption-metadata tw-select-none motion-safe:tw-transition-all motion-safe:tw-duration-fast motion-safe:tw-ease-swift motion-safe:active:tw-scale-[0.98] ${FOCUS_RING} ${colourPair}`, className)}
    >
      <span className="tw-max-w-[160px] tw-truncate" title={label}>
        {label}
      </span>
    </button>
  );
});

export default ChoiceChip;
